import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import {
  FILTER_NUDGE_AFTER_INDEX,
  FILTER_NUDGE_AGE_ICONS,
  FILTER_NUDGE_AGE_LABEL_SUFFIX,
  FILTER_NUDGE_AGE_VALUE_SUFFIX,
  FILTER_NUDGE_GENDER_CHIPS,
  FILTER_NUDGE_MIN_TOTAL_RESULTS,
  FILTER_NUDGE_QUESTION_ORDER,
  FILTER_NUDGE_QUESTIONS,
  FILTER_NUDGE_ROUTER_FILTERS,
  FILTER_NUDGE_SPECIALTY_CHIPS,
  FilterNudgeChipConfig,
  FilterNudgeQuestion,
} from './constants'

export interface FilterNudgeChip {
  label: string
  icon?: string
  /** `global_filters` id written to the URL; null when the state lacks it. */
  id: string | null
  isAvailable: boolean
}

type StateFilterOption = ProviderFilter & { isAvailable?: boolean }

const hasQueryValue = (
  query: NodeJS.Dict<string | string[]>,
  routerFilter: string
): boolean =>
  [routerFilter, routerFilter.replace('[]', '')].some((key) => {
    const value = query[key]

    return Array.isArray(value) ? value.some((entry) => !!entry) : !!value
  })

/** Retained unavailable selections still count as filtered. */
export const getFilterNudgeQuestion = (
  query: NodeJS.Dict<string | string[]>
): FilterNudgeQuestion | null =>
  FILTER_NUDGE_QUESTION_ORDER.find(
    (question) => !hasQueryValue(query, FILTER_NUDGE_ROUTER_FILTERS[question])
  ) ?? null

interface FilterNudgeVisibilityParams {
  isEnabled: boolean
  isMobile: boolean
  isWaterfall: boolean
  totalResults: number
  question: FilterNudgeQuestion | null
  chips: FilterNudgeChip[]
}

export const isFilterNudgeVisible = ({
  isEnabled,
  isMobile,
  isWaterfall,
  totalResults,
  question,
  chips,
}: FilterNudgeVisibilityParams): boolean =>
  isEnabled &&
  isMobile &&
  !isWaterfall &&
  totalResults >= FILTER_NUDGE_MIN_TOTAL_RESULTS &&
  !!question &&
  chips.some((chip) => chip.isAvailable)

/** modifyURLQuery expects keys without brackets; a chip tap resets pagination. */
export const getFilterNudgeQuery = (
  question: FilterNudgeQuestion,
  chipId: string
): Record<string, string | string[]> => ({
  [FILTER_NUDGE_ROUTER_FILTERS[question].replace('[]', '')]: [chipId],
  offset: '0',
})

/** A nudge needs a provider card after it. */
export const getFilterNudgeIndex = (cardsOnPage: number): number | null =>
  cardsOnPage > FILTER_NUDGE_AFTER_INDEX + 1 ? FILTER_NUDGE_AFTER_INDEX : null

const toChip = (
  config: FilterNudgeChipConfig,
  options: StateFilterOption[]
): FilterNudgeChip => {
  const option = options.find((filter) => filter.value === config.value)

  return {
    label: config.label,
    icon: config.icon,
    id: option?.id ?? null,
    isAvailable: !!option && option.isAvailable !== false,
  }
}

/** Keep unavailable genders visible; omit unavailable specialty and age options. */
export const getFilterNudgeChips = (
  question: FilterNudgeQuestion,
  groupedFilters: GroupedProviderFilter
): FilterNudgeChip[] => {
  switch (question) {
    case FILTER_NUDGE_QUESTIONS.SPECIALTY: {
      const options: StateFilterOption[] =
        groupedFilters[ProviderFilterTypes.AREA_OF_CONCERN] ?? []

      return FILTER_NUDGE_SPECIALTY_CHIPS.map((config) =>
        toChip(config, options)
      ).filter((chip) => chip.isAvailable)
    }

    case FILTER_NUDGE_QUESTIONS.GENDER: {
      const options: StateFilterOption[] =
        groupedFilters[ProviderFilterTypes.GENDER] ?? []

      return FILTER_NUDGE_GENDER_CHIPS.map((config) => toChip(config, options))
    }

    case FILTER_NUDGE_QUESTIONS.AGE: {
      const options: StateFilterOption[] =
        groupedFilters[ProviderFilterTypes.CLIENT_AGE_GROUP] ?? []

      return options
        .filter((option) => option.isAvailable !== false)
        .map((option) => {
          const ageGroup = option.value.replace(
            FILTER_NUDGE_AGE_VALUE_SUFFIX,
            ''
          )

          return {
            label: `${ageGroup}${FILTER_NUDGE_AGE_LABEL_SUFFIX}`,
            icon: FILTER_NUDGE_AGE_ICONS[ageGroup],
            id: option.id,
            isAvailable: true,
          }
        })
    }

    default:
      return []
  }
}
